import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { Button, Checkbox, Input, Typography } from 'antd'
import ColorPicker, { Color } from 'antd/es/color-picker'
import Table, { ColumnsType } from 'antd/es/table'
import { HistoStackingGroup } from 'features/drilldown/components/histogram/configPanel/histoStackingTypes'
import {
  STACKING_GROUP_OTHER,
  useHistogramConfiguration,
} from 'features/drilldown/components/histogram/contexts/HistogramConfigurationContext'
import {
  DrilldownFilterView,
  Filter,
  isFilterActive,
} from 'features/drilldown/panels/global/filters/DrilldownFilterView'
import _ from 'lodash'
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react'
import styled from 'styled-components'
import { v4 } from 'uuid'

import { pickDistinctColor } from './stackingColorUtil'

const { Text } = Typography

const AddButtonConatiner = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  margin-top: 10px;
`

const SectionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

function areAnyFiltersActive(filters: ReadonlyArray<Filter>): boolean {
  return _.reduce(
    filters,
    (acc, curr) => acc || isFilterActive(curr.state),
    false
  )
}

/**
 * Component to allow a user to configure their 'stacking groups', to set up
 * stacked bars in a histogram.
 */
export const HistoStackingConfigView: FC = () => {
  const { stackingGroups, setStackingGroups, setSelectedBarIds } =
    useHistogramConfiguration()

  const handleNameChange = useCallback(
    (id: string, newName: string) => {
      setStackingGroups?.((prevGroups) =>
        prevGroups?.map((group) =>
          group.id === id ? { ...group, name: newName } : group
        )
      )
    },
    [setStackingGroups]
  )

  const handleColorChange = useCallback(
    (id: string, newColor: Color) => {
      setStackingGroups?.((prevGroups) =>
        prevGroups?.map((group) =>
          group.id === id ? { ...group, color: newColor.toHexString() } : group
        )
      )
    },
    [setStackingGroups]
  )

  const handleDelete = useCallback(
    (id: string) => {
      setSelectedBarIds((prevSelBarIds) => {
        const delStackIdx = stackingGroups?.findIndex(
          (prevId) => prevId.id === id
        )
        const newSelBarIds =
          delStackIdx !== undefined
            ? prevSelBarIds
                .map(
                  ([barId, stackIdx]) =>
                    stackIdx === delStackIdx || stackIdx === null // if we're deleting the seleced one, clear the selection
                      ? undefined
                      : stackIdx < delStackIdx // if the selection is before the deletion idx, leave it alone
                        ? [barId, stackIdx]
                        : [barId, stackIdx - 1] // if we delete before the selection, we decrement it
                )
                .filter(
                  (sel): sel is [string, number] =>
                    !!sel && (sel as [string, number])[1] >= 0
                )
            : prevSelBarIds
        return newSelBarIds
      })
      setStackingGroups?.((prevGroups) => {
        // update the seleccted bar IDs to reflecct the deletion
        return prevGroups?.filter(
          (group) => group.id === STACKING_GROUP_OTHER.id || group.id !== id
        )
      })
    },
    [setSelectedBarIds, setStackingGroups, stackingGroups]
  )

  const existingColors = useMemo(
    () => stackingGroups?.map((g) => g.color) ?? [],
    [stackingGroups]
  )
  const handleAdd = useCallback(() => {
    const newGroup: HistoStackingGroup = {
      id: v4(),
      name: 'New Group',
      color: pickDistinctColor(existingColors),
      enabled: false,
      filters: [],
    }
    setStackingGroups?.((prevGroups) => {
      // Always make sure the _OTHER group is the last element
      const newGroups = [...(prevGroups ?? []), newGroup].filter(
        (group) => group.id !== STACKING_GROUP_OTHER.id
      )
      return [...newGroups, STACKING_GROUP_OTHER]
    })
  }, [existingColors, setStackingGroups])

  const createEnabledSetter: (
    groupId: string
  ) => Dispatch<SetStateAction<boolean>> = useCallback(
    (groupId) => {
      const groupIdx = stackingGroups?.findIndex((g) => g.id === groupId) ?? -1
      const group = groupIdx >= 0 ? stackingGroups?.[groupIdx] : undefined
      return (val) => {
        return setStackingGroups?.((prev) => {
          if (!group || groupIdx < 0) {
            return prev
          }
          const newEnabled =
            typeof val === 'function' ? val(group.enabled) : val
          const newGroup = {
            ...group,
            enabled: newEnabled,
          }
          const newVal = stackingGroups?.toSpliced(groupIdx, 1, newGroup)
          return newVal
        })
      }
    },
    [setStackingGroups, stackingGroups]
  )

  const columns: ColumnsType<HistoStackingGroup> = useMemo(
    () => [
      {
        title: 'Enabled',
        dataIndex: 'enabled',
        key: 'enabled',
        render: (value, record) => {
          const setEnabled = createEnabledSetter(record.id)
          return (
            <Checkbox
              checked={value}
              disabled={!areAnyFiltersActive(record.filters)}
              onChange={(e) =>
                setEnabled(e.target.checked && !!record.filters.length)
              }
            />
          )
        },
      },
      {
        title: 'Label',
        dataIndex: 'name',
        key: 'name',
        render: (text, record) => (
          <Input
            value={text}
            onChange={(e) => handleNameChange(record.id, e.target.value)}
          />
        ),
      },
      {
        title: 'Color',
        dataIndex: 'color',
        key: 'color',
        render: (color, record) => (
          <ColorPicker
            value={color}
            onChangeComplete={(newColor) =>
              handleColorChange(record.id, newColor)
            }
          />
        ),
      },
      {
        title: 'Actions',
        key: 'actions',
        render: (_, record) =>
          record.id !== STACKING_GROUP_OTHER.id && (
            <Button
              type='text'
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id)}
            />
          ),
      },
    ],
    [createEnabledSetter, handleNameChange, handleColorChange, handleDelete]
  )

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([])
  const createFilterSetter: (
    groupId: string
  ) => Dispatch<SetStateAction<ReadonlyArray<Filter>>> = useCallback(
    (groupId) => {
      const groupIdx = stackingGroups?.findIndex((g) => g.id === groupId) ?? -1
      const group = groupIdx >= 0 ? stackingGroups?.[groupIdx] : undefined
      return (val) => {
        return setStackingGroups?.((prev) => {
          if (!group || groupIdx < 0) {
            return prev
          }
          const newFilters =
            typeof val === 'function' ? val(group.filters) : val

          let newEnabled = group.enabled
          // If it was previously empty, enable it
          if (
            !areAnyFiltersActive(group.filters) &&
            areAnyFiltersActive(newFilters)
          ) {
            newEnabled = true
          }
          if (
            groupId !== STACKING_GROUP_OTHER.id &&
            !areAnyFiltersActive(newFilters)
          ) {
            newEnabled = false
          }
          const newGroup = {
            ...group,
            filters: newFilters,
            enabled: newEnabled,
          }
          const newVal = stackingGroups?.toSpliced(groupIdx, 1, newGroup)
          return newVal
        })
      }
    },
    [setStackingGroups, stackingGroups]
  )
  const filterSettingFuncs = useMemo(
    () =>
      Object.fromEntries(
        stackingGroups
          ?.map((group) => group.id)
          .map((id) => [id, createFilterSetter(id)]) ?? []
      ),
    [createFilterSetter, stackingGroups]
  )

  return (
    <SectionContainer>
      <Text strong>Stacks: </Text>
      <Table
        rowKey='id'
        columns={columns}
        dataSource={stackingGroups}
        pagination={false}
        expandable={{
          expandedRowRender: (record) => {
            if (record.id === STACKING_GROUP_OTHER.id) {
              return undefined
            }
            const filters = record.filters
            const setFilters = filterSettingFuncs[record.id]
            return (
              <DrilldownFilterView filters={filters} setFilters={setFilters} />
            )
          },
          expandedRowKeys: expandedRowKeys,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(expanded ? [record.id] : [])
          },
        }}
      />
      <AddButtonConatiner>
        <Button
          type='default'
          shape='circle'
          icon={<PlusOutlined />}
          onClick={handleAdd}
        />
      </AddButtonConatiner>
    </SectionContainer>
  )
}
